import { AC_STEP_FORGOT_USERNAME } from "../../utils/AccountCreationConstants";

import useAccountCreationData from "../../../../../../state/landingPages/accountCreation/UseAccountCreationData";
import useCrossUINavigation from "@usas/ui-web/state/crossUINavigation/UseCrossUINavigation";

import { useAccountCreationAPI } from "./useAccountCreationAPI";
import { useAccountCreationFlow } from "./useAccountCreationFlow";
import {
  isUserUnderMinimumAge,
  getUserInfoDataWithBirthDate,
  prepareCreateStep1Data
} from "./utils";

/**
 * Main hook for managing account creation user info flow
 * Orchestrates API calls, business logic, and UI state management
 */
const useAccountCreationUserInfo = () => {
  const { navigateToLandingPage } = useCrossUINavigation();

  const {
    accountCreationState,
    accountCreationStep1State,
    updateAccountCreationStep,
    updateAccountCreationPageData,
    resetAccountCreationStep1State,
    postAccountCreationStep1,
    postInviteUser
  } = useAccountCreationData();

  const {
    apiErrorPopupState,
    clearAPIError,
    handleAPICall,
    callInviteUser,
    isLoading: isAPILoading
  } = useAccountCreationAPI();

  const {
    navigateBack,
    proceedToNextStep,
    handleCreateStep1Response,
    handleInviteUserAndProceed
  } = useAccountCreationFlow({
    accountCreationState,
    updateAccountCreationStep,
    callInviteUser,
    postInviteUser
  });

  // Get current user info data with birth date merged
  const currentUserInfoData = getUserInfoDataWithBirthDate(accountCreationState);

  // Check if current user is under minimum age
  const isCurrentUserUnder16 = () => {
    const birthDate = accountCreationState.confirmAgeData?.birthDate;
    return isUserUnderMinimumAge(birthDate);
  };

  // Event Handlers
  const handleBackClick = (formState) => {
    const backNavigation = navigateBack(formState);

    if (backNavigation.navigateToLanding) {
      navigateToLandingPage();
    } else {
      updateAccountCreationStep(
        backNavigation.step,
        backNavigation.data,
        backNavigation.dataKey
      );
    }
  };

  const handleDupCheckForgotUsername = () => {
    resetAccountCreationStep1State();
    updateAccountCreationStep(AC_STEP_FORGOT_USERNAME);
  };

  const handleDupCheckCancel = () => {
    resetAccountCreationStep1State();
  };

  const handleCloseAPIErrorPopup = () => {
    resetAccountCreationStep1State();
    clearAPIError();
  };

  const handleDupCheckContinue = async (memberId = undefined) => {
    const userInfoData = currentUserInfoData;

    if (memberId) {
      // User selected a specific person to link to
      const userInfoDataWithMemberId = { ...userInfoData, linkMemberId: memberId };
      updateAccountCreationPageData(userInfoDataWithMemberId, 'userInfoData');

      // Call CreateStep1 API again with the selected memberId - this IS a linking scenario
      const createStep1Data = prepareCreateStep1Data(userInfoDataWithMemberId, accountCreationState);
      await executeCreateStep1(createStep1Data, userInfoDataWithMemberId, true);
    } else {
      // User chose to continue without linking
      const createStep1Data = prepareCreateStep1Data(userInfoData, accountCreationState);

      await executeCreateStep1(createStep1Data, userInfoData, true);
      // if (isCurrentUserUnder16()) {
      //   proceedToNextStep(userInfoData);
      // } else {
      //   await handleInviteUserAndProceed(userInfoData);
      // }
    }
  };

  const handleFormSubmit = async (formState) => {
    // Merge form data with existing state
    const mergedUserInfoData = prepareMergedUserInfoData(formState);

    // Save user info data first
    updateAccountCreationPageData(mergedUserInfoData, 'userInfoData');

    // Check if user is under 16 - if so, delay CreateStep1 until after YouthContact form
    const isUnder16 = isCurrentUserUnder16();
    
    if (isUnder16) {
      // For users under 16, just proceed to YouthContact form without calling CreateStep1
      // CreateStep1 will be called after YouthContact form is completed
      proceedToNextStep(mergedUserInfoData);
    } else {
      // For users 16+, call CreateStep1 API immediately - this is NOT a linking scenario (initial form submission)
      const createStep1Data = prepareCreateStep1Data(mergedUserInfoData, accountCreationState);
      await executeCreateStep1(createStep1Data, mergedUserInfoData, false); // false = not a linking scenario
    }
  };

  // Helper Functions
  const prepareMergedUserInfoData = (formState) => {
    const birthDate = accountCreationState.confirmAgeData?.birthDate || formState.birthDate;
    const formStateWithBirthDate = { ...formState, birthDate };
    const existingUserInfo = accountCreationState.userInfoData || {};

    return {
      ...existingUserInfo,
      ...formStateWithBirthDate
    };
  };

  const executeCreateStep1 = async (createStep1Data, userInfoData, ignoreMatches = false) => {
    const postAccountCreationStep1Promise = postAccountCreationStep1(createStep1Data, ignoreMatches);

    await handleAPICall(
      postAccountCreationStep1Promise,
      (response) => handleCreateStep1Success(response, userInfoData, ignoreMatches),
      'An error occurred during account creation.'
    );
  };

  const handleCreateStep1Success = (response, userInfoData, ignoreMatches = false) => {
    const result = handleCreateStep1Response(response, userInfoData, () => proceedToNextStep(userInfoData), ignoreMatches);

    if (result.error) {
      throw new Error(result.error);
    }

    if (result.callInviteUser) {
      handleInviteUserAndProceed(userInfoData);
    }

    // For showDupPopup and proceedDirectly, the flow is handled by the response handler
  };

  // Return hook interface
  return {
    // Loading states
    isLoading: accountCreationStep1State.isObjLoading || isAPILoading,

    // Data
    userInfoData: currentUserInfoData,
    childData: accountCreationState.childData,
    isUserUnder16: isCurrentUserUnder16(),

    // UI States  
    apiErrorPopupState,
    dupCheckState: accountCreationStep1State,

    // Event Handlers
    onBackClicked: handleBackClick,
    onDupCheckPopupCancelClicked: handleDupCheckCancel,
    onDupCheckPopupForgotUsernameClicked: handleDupCheckForgotUsername,
    onDupCheckPopupContinueClicked: handleDupCheckContinue,
    onCloseAPIErrorPopupClicked: handleCloseAPIErrorPopup,
    onSubmitFormCallback: handleFormSubmit,

    // Helper functions for other hooks to use
    executeCreateStep1,
    prepareCreateStep1Data,
    prepareMergedUserInfoData
  };
};

export default useAccountCreationUserInfo;